import {
  PublishingSplitData,
  RecordingSplitData,
  SplitData,
  hasSplitFieldValue,
} from '@disco/state-management/reducers/rightsManagement'
import type {
  RightsHolderEntity,
  RightsSplitMemberRequest,
  SplitValidationError,
} from '@disco/types/types/rights-data'

import type { SearchableDropdownOption } from 'components/ds/SearchableDropdown'

export const findErrorsForField = (
  validationError: SplitValidationError | undefined,
  fieldTypes: string[],
) =>
  fieldTypes.map(
    fieldType =>
      validationError?.fieldErrors.find(fe => fe.field === fieldType)
        ?.messages[0],
  )

/**
 * Maps raw backend rights-split error strings to user-friendly messages.
 * Falls back to the original message when no known pattern matches.
 */
export const humanizeBackendError = (raw: string): string => {
  if (raw.includes('Total rights percentage exceeds')) {
    return 'Total rights splits exceed 100%'
  }
  if (raw.includes('combination already exists')) {
    return 'Duplicate writer and publisher right split combination'
  }
  return raw
}

/**
 * Filters and maps rights holder entities to dropdown options based on search text
 */
export const filterRightsHolderOptions = (
  entities: RightsHolderEntity[],
  searchText: string,
): SearchableDropdownOption<RightsHolderEntity>[] => {
  const searchLower = searchText.toLowerCase().trim()
  return entities
    .filter(e => !searchLower || e.title.toLowerCase().includes(searchLower))
    .map(e => ({
      id: String(e.id),
      label: e.title,
      data: e,
    }))
}

/**
 * Transform SplitData to RightsSplitMemberRequest array for API request
 * Exported for use by parent component in save handlers.
 */
function transformPublishingSplitToMembers(
  split: PublishingSplitData,
): RightsSplitMemberRequest[] {
  const members: RightsSplitMemberRequest[] = []

  if (hasSplitFieldValue(split.writerId)) {
    members.push({ entity_id: split.writerId, role_type: 'RightsHolder' })
  }
  if (hasSplitFieldValue(split.publisherId)) {
    members.push({
      entity_id: split.publisherId,
      role_type: 'RightsController',
    })
  }
  if (hasSplitFieldValue(split.adminId)) {
    members.push({ entity_id: split.adminId, role_type: 'Administrator' })
  }
  if (hasSplitFieldValue(split.proId)) {
    members.push({ entity_id: split.proId, role_type: 'PRO' })
  }

  return members
}

function transformRecordingSplitToMembers(
  split: RecordingSplitData,
): RightsSplitMemberRequest[] {
  const members: RightsSplitMemberRequest[] = []

  if (hasSplitFieldValue(split.rightsholderId)) {
    members.push({ entity_id: split.rightsholderId, role_type: 'RightsHolder' })
  }

  if (hasSplitFieldValue(split.labelId)) {
    members.push({ entity_id: split.labelId, role_type: 'Label' })
  }

  return members
}

export function transformSplitToMembers(
  split: SplitData,
): RightsSplitMemberRequest[] {
  if (split.kind === 'recording') {
    return transformRecordingSplitToMembers(split as RecordingSplitData)
  } else {
    return transformPublishingSplitToMembers(split as PublishingSplitData)
  }
}

export const toChains = <T extends PublishingSplitData | RecordingSplitData>(
  splits: T[],
  isValid: (s: T) => boolean,
) =>
  splits.filter(isValid).map(split => ({
    rights_percentage:
      split.split === null ? null : Math.round(split.split * 100),
    members: transformSplitToMembers(split),
  }))
